import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Download, Loader2, RefreshCw, Calendar, Truck, Package, CheckCircle, Clock, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { listSites } from '@/app/services/siteService';
import { Site } from '@/app/types/site';
import {
  fetchDeliveryMetrics,
  fetchDeliveryJourney,
  fetchStagePerformance,
  fetchChallanMetrics,
  DeliveryMetricsItem,
  DeliveryJourneyItem,
  StagePerformanceItem,
  ChallanMetrics,
  DeliveryMetricsParams
} from '@/app/services/supabaseEdgeFunctionService';

const DeliveryMetricsPage: React.FC = () => {
  // State management
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Last 7 days
    return format(date, 'yyyy-MM-dd');
  });
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [period, setPeriod] = useState('daily');
  const [siteId, setSiteId] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Prepare params for API calls
  const apiParams = {
    start_date: startDate,
    end_date: endDate,
    period,
    ...(siteId && { site_id: siteId })
  };

  const journeyApiParams = {
    start_date: startDate,
    end_date: endDate,
    ...(siteId && { site_id: siteId })
  };

  // Fetch sites for dropdown
  const { data: sitesData = { items: [] }, isLoading: isLoadingSites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 }),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Set the first site as default when sites are loaded
  useEffect(() => {
    if (!isInitialized && sitesData.items && sitesData.items.length > 0 && siteId === null) {
      setSiteId(sitesData.items[0].id);
      setIsInitialized(true);
    }
  }, [sitesData.items, siteId, isInitialized]);

  // State change handlers with auto-refresh
  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    // Queries will auto-refresh due to key changes
  };

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    // Queries will auto-refresh due to key changes
  };

  const handleSiteChange = (value: string) => {
    setSiteId(parseInt(value));
    // Queries will auto-refresh due to key changes
  };

  // Fetch delivery metrics data using React Query
  const { data: metricsData = [], isLoading: isLoadingMetrics, refetch: refetchMetrics } = useQuery({
    queryKey: ['delivery-metrics', apiParams],
    queryFn: () => fetchDeliveryMetrics(apiParams as DeliveryMetricsParams),
    enabled: !!siteId, // Only fetch when a site is selected
    retry: 1,
    onError: (error: any) => {
      toast.error(error.message || 'Failed to fetch delivery metrics');
    }
  });

  // Fetch journey data
  const { data: journeyData = [], isLoading: isLoadingJourney, refetch: refetchJourney } = useQuery({
    queryKey: ['delivery-journey', journeyApiParams],
    queryFn: () => fetchDeliveryJourney(journeyApiParams),
    enabled: !!siteId, // Only fetch when a site is selected
    retry: 1,
    onError: (error: any) => {
      toast.error(error.message || 'Failed to fetch delivery journey data');
    }
  });

  // Fetch stage performance data
  const { data: stageData = [], isLoading: isLoadingStage, refetch: refetchStage } = useQuery({
    queryKey: ['stage-performance', apiParams],
    queryFn: () => fetchStagePerformance(apiParams as DeliveryMetricsParams),
    enabled: !!siteId, // Only fetch when a site is selected
    retry: 1,
    onError: (error: any) => {
      toast.error(error.message || 'Failed to fetch stage performance data');
    }
  });

  // Fetch challan metrics
  const { data: challanData, isLoading: isLoadingChallan, refetch: refetchChallan } = useQuery({
    queryKey: ['challan-metrics', journeyApiParams],
    queryFn: () => fetchChallanMetrics(journeyApiParams),
    enabled: !!siteId, // Only fetch when a site is selected
    retry: 1,
    onError: (error: any) => {
      toast.error(error.message || 'Failed to fetch challan metrics');
    }
  });

  // Combined loading state
  const isLoading = isLoadingMetrics || isLoadingJourney || isLoadingStage || isLoadingChallan;

  // Manual refresh function
  const handleRefresh = async () => {
    await Promise.all([
      refetchMetrics(),
      refetchJourney(),
      refetchStage(),
      refetchChallan()
    ]);
    toast.success('Data refreshed successfully');
  };

  // Date preset handler
  const handleDatePreset = (preset: string) => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    
    switch (preset) {
      case 'yesterday':
        handleDateChange(format(yesterday, 'yyyy-MM-dd'), format(yesterday, 'yyyy-MM-dd'));
        break;
      case 'last7days':
        handleDateChange(format(subDays(today, 6), 'yyyy-MM-dd'), format(today, 'yyyy-MM-dd'));
        break;
      case 'last30days':
        handleDateChange(format(subDays(today, 29), 'yyyy-MM-dd'), format(today, 'yyyy-MM-dd'));
        break;
      case 'thisMonth':
        handleDateChange(format(startOfMonth(today), 'yyyy-MM-dd'), format(endOfMonth(today), 'yyyy-MM-dd'));
        break;
    }
  };

  // Export function
  const handleExport = () => {
    // Create comprehensive CSV data
    const csvContent = [
      // Headers
      ['Delivery Metrics Report'],
      [`Period: ${startDate} to ${endDate}`],
      [`Aggregation: ${period}`],
      [''],
      
      // Summary metrics
      ['Summary Metrics'],
      ['Period', 'Total Invoices', 'Delivered', 'Delivery Rate', 'Avg Delivery Time (hrs)', 'Total Value', 'Delivered Value'],
      ...metricsData.map(m => [
        m.period_label,
        m.total_invoices || 0,
        m.invoices_delivered || 0,
        `${(m.delivery_rate || 0).toFixed(2)}%`,
        (m.avg_invoice_to_delivery_hours || 0).toFixed(2),
        (m.total_invoice_value || 0).toFixed(2),
        (m.delivered_value || 0).toFixed(2)
      ]),
      [''],
      
      // Journey breakdown
      ['Delivery Journey'],
      ['Stage', 'Count', 'Percentage', 'Avg Time to Stage (hrs)', 'Total Value'],
      ...journeyData.map(j => [
        j.stage,
        j.count || 0,
        `${(j.percentage || 0).toFixed(2)}%`,
        j.avg_time_to_stage_hours?.toFixed(2) || 'N/A',
        (j.total_value || 0).toFixed(2)
      ]),
      [''],
      
      // Stage performance
      ['Stage Performance'],
      ['Period', 'Invoice to Packed (hrs)', 'Packed to Shipped (hrs)', 'Shipped to Delivered (hrs)', 'Total Cycle Time (hrs)', 'Efficiency Score'],
      ...stageData.map(s => [
        s.period_label,
        (s.invoice_to_packed_hours || 0).toFixed(2),
        (s.packed_to_shipped_hours || 0).toFixed(2),
        (s.shipped_to_delivered_hours || 0).toFixed(2),
        (s.total_cycle_time_hours || 0).toFixed(2),
        `${(s.efficiency_score || 0).toFixed(2)}%`
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `delivery-metrics-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  // Currency formatter
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Calculate summary metrics
  const summaryMetrics = metricsData.reduce((acc, curr) => ({
    totalInvoices: acc.totalInvoices + (curr.total_invoices || 0),
    totalDelivered: acc.totalDelivered + (curr.invoices_delivered || 0),
    totalValue: acc.totalValue + (curr.total_invoice_value || 0),
    deliveredValue: acc.deliveredValue + (curr.delivered_value || 0)
  }), { totalInvoices: 0, totalDelivered: 0, totalValue: 0, deliveredValue: 0 });

  const overallDeliveryRate = summaryMetrics.totalInvoices > 0 
    ? (summaryMetrics.totalDelivered / summaryMetrics.totalInvoices) * 100 
    : 0;

  const avgDeliveryTime = metricsData.length > 0
    ? metricsData.reduce((sum, m) => sum + (m.avg_invoice_to_delivery_hours || 0), 0) / metricsData.length
    : 0;

  // Chart colors
  const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#ef4444'];

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Delivery Performance Analytics
          </CardTitle>
          <CardDescription>
            Track and analyze your delivery operations from invoice to fulfillment
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Quick Date Presets */}
          <div className="flex gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('yesterday')}>
              Yesterday
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('last7days')}>
              Last 7 Days
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('last30days')}>
              Last 30 Days
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('thisMonth')}>
              This Month
            </Button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Start Date */}
            <div className="space-y-2">
              <label htmlFor="startDate" className="text-sm font-medium">Start Date</label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange(e.target.value, endDate)}
                className="w-full"
              />
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <label htmlFor="endDate" className="text-sm font-medium">End Date</label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange(startDate, e.target.value)}
                className="w-full"
              />
            </div>

            {/* Period */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Period</label>
              <Select value={period} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Site Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Site</label>
              <Select
                value={siteId?.toString() || ''}
                onValueChange={handleSiteChange}
                disabled={isLoadingSites}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sitesData.items?.map((site: Site) => (
                    <SelectItem key={site.id} value={site.id.toString()}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Buttons */}
            <div className="flex items-end gap-2">
              <Button onClick={handleRefresh} disabled={isLoading}>
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Refresh
              </Button>
              <Button onClick={handleExport} variant="outline" disabled={isLoading || metricsData.length === 0}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Invoices</p>
                <p className="text-2xl font-bold">{summaryMetrics.totalInvoices}</p>
              </div>
              <Package className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Delivery Success Rate</p>
                <p className="text-2xl font-bold">{overallDeliveryRate.toFixed(1)}%</p>
              </div>
              {overallDeliveryRate >= 90 ? (
                <TrendingUp className="h-8 w-8 text-green-500" />
              ) : (
                <TrendingDown className="h-8 w-8 text-red-500" />
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Delivery Time</p>
                <p className="text-2xl font-bold">{avgDeliveryTime.toFixed(1)} hrs</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Delivered Value</p>
                <p className="text-2xl font-bold">{formatCurrency(summaryMetrics.deliveredValue)}</p>
                <p className="text-xs text-muted-foreground">of {formatCurrency(summaryMetrics.totalValue)}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Analytics */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="journey">Delivery Journey</TabsTrigger>
          <TabsTrigger value="performance">Stage Performance</TabsTrigger>
          <TabsTrigger value="challans">Challan Metrics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Delivery Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice to Delivery Performance</CardTitle>
              <CardDescription>Track invoices from creation to successful delivery</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : metricsData.length > 0 ? (
                <ChartContainer config={{}} className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={metricsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period_label" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="total_invoices" stroke="#3b82f6" name="Total Invoices" strokeWidth={2} />
                      <Line yAxisId="left" type="monotone" dataKey="invoices_delivered" stroke="#10b981" name="Delivered" strokeWidth={3} />
                      <Line yAxisId="right" type="monotone" dataKey="avg_invoice_to_delivery_hours" stroke="#f59e0b" name="Avg Delivery Time (hrs)" strokeDasharray="5 5" />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available for the selected period
                </div>
              )}
            </CardContent>
          </Card>

          {/* Volume Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice Volume by Status</CardTitle>
              <CardDescription>Distribution of invoices across delivery stages</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : metricsData.length > 0 ? (
                <ChartContainer config={{}} className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metricsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period_label" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="invoices_pending" stackId="a" fill="#ef4444" name="Pending" />
                      <Bar dataKey="invoices_packed" stackId="a" fill="#f59e0b" name="Packed" />
                      <Bar dataKey="invoices_shipped" stackId="a" fill="#3b82f6" name="Shipped" />
                      <Bar dataKey="invoices_delivered" stackId="a" fill="#10b981" name="Delivered" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available for the selected period
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="journey" className="space-y-4">
          {/* Journey Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery Journey Funnel</CardTitle>
              <CardDescription>Invoice progression through delivery stages</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-96 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : journeyData.length > 0 ? (
                <div className="space-y-4">
                  {journeyData.map((stage, index) => (
                    <div key={stage.stage} className="relative">
                      <div className="flex items-center gap-4">
                        <div className="w-32 font-medium">{stage.stage}</div>
                        <div className="flex-1">
                          <Progress value={stage.percentage} className="h-8" />
                        </div>
                        <div className="w-24 text-right">
                          <div className="font-medium">{stage.count}</div>
                          <div className="text-sm text-muted-foreground">{(stage.percentage || 0).toFixed(1)}%</div>
                        </div>
                        <div className="w-32 text-right">
                          <div className="text-sm text-muted-foreground">Value</div>
                          <div className="font-medium">{formatCurrency(stage.total_value)}</div>
                        </div>
                        <div className="w-24 text-right">
                          {stage.avg_time_to_stage_hours && (
                            <>
                              <div className="text-sm text-muted-foreground">Avg Time</div>
                              <div className="font-medium">{(stage.avg_time_to_stage_hours || 0).toFixed(1)} hrs</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No journey data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Journey Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Stage Distribution</CardTitle>
              <CardDescription>Current distribution of invoices across stages</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : journeyData.length > 0 ? (
                <ChartContainer config={{}} className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={journeyData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.stage}: ${(entry.percentage || 0).toFixed(1)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {journeyData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          {/* Stage Performance Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice to Delivery Time</CardTitle>
              <CardDescription>Total time from invoice creation to delivery completion</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : stageData.length > 0 ? (
                <ChartContainer config={{}} className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stageData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period_label" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="total_cycle_time_hours" 
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        name="Invoice to Delivered (hrs)" 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No performance data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Efficiency Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Delivery Efficiency Trend</CardTitle>
                <CardDescription>Efficiency score over time</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="h-64 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : stageData.length > 0 ? (
                  <ChartContainer config={{}} className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stageData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period_label" />
                        <YAxis />
                        <Tooltip />
                        <Area 
                          type="monotone" 
                          dataKey="efficiency_score" 
                          stroke="#10b981" 
                          fill="#10b981" 
                          fillOpacity={0.3}
                          name="Efficiency Score (%)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stage Breakdown Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Stage Time Breakdown</CardTitle>
                <CardDescription>Time distribution across delivery stages</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="h-64 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : stageData.length > 0 ? (
                  <ChartContainer config={{}} className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stageData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period_label" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="invoice_to_packed_hours" fill="#ef4444" name="To Packed" />
                        <Bar dataKey="packed_to_shipped_hours" fill="#f59e0b" name="To Shipped" />
                        <Bar dataKey="shipped_to_delivered_hours" fill="#10b981" name="To Delivered" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Performance Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Performance Metrics</CardTitle>
              <CardDescription>Complete delivery cycle breakdown by period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-center bg-primary/5">Invoice → Delivered</TableHead>
                      <TableHead className="text-center text-muted-foreground">To Packed</TableHead>
                      <TableHead className="text-center text-muted-foreground">To Shipped</TableHead>
                      <TableHead className="text-center text-muted-foreground">To Delivered</TableHead>
                      <TableHead className="text-center">Efficiency Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8">
                          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : stageData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No performance data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      stageData.map((stage) => (
                        <TableRow key={stage.period_start}>
                          <TableCell className="font-medium">{stage.period_label}</TableCell>
                          <TableCell className="text-center bg-primary/5">
                            <Badge variant="default" className="font-bold">
                              {(stage.total_cycle_time_hours || 0).toFixed(1)} hrs
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-sm text-muted-foreground">
                              {(stage.invoice_to_packed_hours || 0).toFixed(1)} hrs
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-sm text-muted-foreground">
                              {(stage.packed_to_shipped_hours || 0).toFixed(1)} hrs
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-sm text-muted-foreground">
                              {(stage.shipped_to_delivered_hours || 0).toFixed(1)} hrs
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge 
                              variant={
                                (stage.efficiency_score || 0) >= 90 ? "default" : 
                                (stage.efficiency_score || 0) >= 70 ? "secondary" : 
                                "destructive"
                              }
                            >
                              {(stage.efficiency_score || 0).toFixed(1)}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="challans" className="space-y-4">
          {/* Challan Metrics Cards */}
          {challanData ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">Total Challans</p>
                      <p className="text-3xl font-bold">{challanData.total_challans}</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>Packed</span>
                          <span className="font-medium">{challanData.challans_packed}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Shipped</span>
                          <span className="font-medium">{challanData.challans_shipped}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Delivered</span>
                          <span className="font-medium">{challanData.challans_delivered}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">Challan Rates</p>
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm">Packing Rate</span>
                            <span className="text-sm font-medium">{(challanData.packing_rate || 0).toFixed(1)}%</span>
                          </div>
                          <Progress value={challanData.packing_rate} className="h-2" />
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm">Shipping Rate</span>
                            <span className="text-sm font-medium">{(challanData.shipping_rate || 0).toFixed(1)}%</span>
                          </div>
                          <Progress value={Math.min(challanData.shipping_rate, 100)} className="h-2" />
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm">Delivery Completion</span>
                            <span className="text-sm font-medium">{(challanData.delivery_completion_rate || 0).toFixed(1)}%</span>
                          </div>
                          <Progress value={challanData.delivery_completion_rate} className="h-2" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">Invoice Coverage</p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-sm">With Challans</span>
                          </div>
                          <span className="font-medium">{challanData.invoices_with_challans}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-red-500" />
                            <span className="text-sm">Without Challans</span>
                          </div>
                          <span className="font-medium">{challanData.invoices_without_challans}</span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="text-sm text-muted-foreground">Coverage Rate</div>
                          <div className="text-2xl font-bold">
                            {challanData.invoices_with_challans + challanData.invoices_without_challans > 0
                              ? ((challanData.invoices_with_challans / (challanData.invoices_with_challans + challanData.invoices_without_challans)) * 100).toFixed(1)
                              : '0.0'}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {challanData.invoices_without_challans > 0 && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-orange-900">Missing Delivery Documentation</p>
                        <p className="text-sm text-orange-800 mt-1">
                          {challanData.invoices_without_challans} invoices do not have associated delivery challans. 
                          This may impact tracking and compliance.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No challan data available
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DeliveryMetricsPage;